import React from "react";
const OLD_DATE = new Date(2000, 0, 1);
const stringSort = (a, b) => a < b ? -1 : a > b ? 1 : 0;
const otherSort = (a, b) => a - b;
const dateSort = (a, b) => {
    const x = a || OLD_DATE;
    const y = b || OLD_DATE;
    return x < y ? -1 : x > y ? 1 : 0;
};
const getSortFn = (dataType) => {
    if (dataType === "boolean" || dataType === "number")
        return otherSort;
    else if (dataType === "date")
        return dateSort;
    else
        return stringSort;
};
const sort = (data, sortBy, sortDir, dataType) => {
    if (!sortBy)
        return data;
    const mew = data.slice();
    const sortFn = getSortFn(dataType);
    mew.sort((a, b) => {
        const value = sortFn(a[sortBy], b[sortBy]);
        return value * (sortDir === "desc" ? -1 : 1);
    });
    return mew;
};
const sortHierarchy = (treeItems, sortBy, sortDir, dataType) => {
    if (!sortBy)
        return treeItems;
    const multiplier = sortDir === "asc" ? 1 : -1;
    const sortFn = getSortFn(dataType);
    const sortTreeFn = (a, b) => {
        const left = a.item[sortBy];
        const right = b.item[sortBy];
        return sortFn(left, right) * multiplier;
    };
    const sortTree = (data) => {
        data.sort(sortTreeFn);
        for (let i = 0; i < data.length; i++) {
            const children = data[i].children;
            if (children && children.length > 0) {
                sortTree(children);
            }
        }
    };
    sortTree(treeItems);
    return buildFlat(treeItems).map(v => v.item);
};
const filterFn = (item, args) => {
    for (const key in args) {
        const { operator, values: filterValues } = args[key];
        if (filterValues.length === 0)
            return true;
        const fv = filterValues[0];
        const value = item[key];
        if (operator === "Between") {
            if (value < fv || value > filterValues[1])
                return false;
        }
        else if (operator === "Contains")
            return false;
        else if (operator === "EQ") {
            if (value !== fv)
                return false;
        }
        else if (operator === "EndsWith") {
            if (!value.endsWith)
                return false;
        }
        else if (operator === "StartsWith") {
            if (!value.startsWith)
                return false;
        }
        else if (operator === "GT") {
            if (value <= fv)
                return false;
        }
        else if (operator === "GTE") {
            if (value < fv)
                return false;
        }
        else if (operator === "LT") {
            if (value >= fv)
                return false;
        }
        else if (operator === "LTE") {
            if (value > fv)
                return false;
        }
        else if (operator === "NE") {
            if (value === fv)
                return false;
        }
        else if (operator === "In") {
            if (!filterValues.includes(value))
                return false;
        }
    }
    return true;
};
export const useFilter = (items, initFilterArgs = {}) => {
    const [filterArgs, setFilterArgsState] = React.useState(initFilterArgs);
    const filteredItems = React.useMemo(() => {
        const hasFilter = Object.keys(filterArgs).length > 0;
        return hasFilter ? items.filter(item => filterFn(item, filterArgs)) : items;
    }, [filterArgs, items]);
    const setFilterArgs = (value) => {
        setFilterArgsState({ ...filterArgs, ...value });
    };
    return [filteredItems, filterArgs, setFilterArgs];
};
export const useHierarchySort = (items, initSortColumn, keepLinear = false) => {
    const [sortBy, setSortBy] = React.useState(initSortColumn.sortBy);
    const [sortDir, setSortDir] = React.useState(initSortColumn.sortDir || "asc");
    const [sortDataType, setSortDataType] = React.useState(initSortColumn.sortDataType || "string");
    const [keepLinear2, setKeepLinear] = React.useState(keepLinear);
    const setSortInfo = React.useCallback((sortColumn, keepLinear = false) => {
        setSortBy(sortColumn.sortBy);
        setSortDir(sortColumn.sortDir || "asc");
        setSortDataType(sortColumn.sortDataType || "string");
        setKeepLinear(keepLinear);
    }, []);
    const treeItems = React.useMemo(() => flatToHierarchy(items, "fullName"), [items]);
    const sortedItems = React.useMemo(() => {
        if (!keepLinear2)
            return sortHierarchy(treeItems, sortBy, sortDir, sortDataType || "string");
        else
            return sort(items, sortBy, sortDir, sortDataType);
    }, [items, keepLinear2, sortBy, sortDataType, sortDir, treeItems]);
    return [sortedItems, { sortBy, sortDir, sortDataType }, setSortInfo];
};
export const useSort = (items, initSortColumn, isIntiallySorted = false) => {
    const [sortBy, setSortBy] = React.useState(initSortColumn.sortBy);
    const [sortDir, setSortDir] = React.useState(initSortColumn.sortDir || "asc");
    const [sortDataType, setSortDataType] = React.useState(initSortColumn.sortDataType || "string");
    const skipSortRef = React.useRef(isIntiallySorted);
    const setSortInfo = React.useCallback((sortColumn) => {
        setSortBy(sortColumn.sortBy);
        setSortDir(sortColumn.sortDir || "asc");
        setSortDataType(sortColumn.sortDataType || "string");
    }, []);
    const sortedItems = React.useMemo(() => {
        if (skipSortRef.current) {
            skipSortRef.current = false;
            return items;
        }
        return sort(items, sortBy, sortDir, sortDataType);
    }, [items, sortBy, sortDataType, sortDir]);
    return [sortedItems, { sortBy, sortDir, sortDataType }, setSortInfo];
};
export class TreeItem {
    constructor(item) {
        this.item = item;
        this.isExpanded = true;
    }
}
export function buildFlat(_items) {
    const flatNodes = [];
    const buildFlatInternal = (list) => {
        for (const item of list) {
            flatNodes.push(item);
            if (item.isExpanded && item.children && item.children.length > 0) {
                buildFlatInternal(item.children);
            }
        }
    };
    buildFlatInternal(_items);
    return flatNodes;
}
function flatToHierarchy(items, fullNameField) {
    if (!items || items.length === 0) {
        return [];
    }
    items.sort((a, b) => stringSort(a[fullNameField], b[fullNameField]));
    const firstTreeItem = new TreeItem(items[0]);
    firstTreeItem.subLevel = 0;
    const stack = [firstTreeItem];
    const _items = [firstTreeItem];
    for (let i = 1; i < items.length; i++) {
        const item = items[i];
        const regEx = item[fullNameField].match(/(.*):(.*)/);
        if (!regEx) {
            const childItem = new TreeItem(item);
            stack.splice(0, stack.length, childItem);
            _items.push(childItem);
            continue;
        }
        const parentFullName = regEx[1];
        for (let j = stack.length - 1; j >= 0; j--) {
            const lastItem = stack[j];
            if (parentFullName === lastItem.item[fullNameField]) {
                const childItem = new TreeItem(item);
                if (!lastItem.children)
                    lastItem.children = [];
                lastItem.children.push(childItem);
                stack.push(childItem);
            }
            else {
                stack.pop();
            }
            if (j === 0 && stack.length === 0) {
                const childItem = new TreeItem(item);
                _items.push(childItem);
                stack.push(childItem);
            }
        }
    }
    return _items;
}
//---------------------------dep------------------------------------
export const useSortDep = (items, initSortBy, hasFilter = false, options = {}) => {
    const [sortBy, setSortBy] = React.useState(initSortBy.sortBy);
    const [sortDir, setSortDir] = React.useState(initSortBy.sortDir || "asc");
    const [sortDataType, setSortDataType] = React.useState(initSortBy.dataType || "string");
    const { idField, parentIdField, hierarchyDataKey } = options;
    const setSortInfo = React.useCallback((sortBy, sortDirection, dataType) => {
        setSortBy(sortBy);
        setSortDir(sortDirection);
        setSortDataType(dataType);
    }, []);
    const sortedItems = React.useMemo(() => {
        if (!hasFilter && sortBy === hierarchyDataKey && sortDir === "desc" && idField && parentIdField)
            return sortHierarchyDep(items, idField, parentIdField, sortBy);
        else
            return sort(items, sortBy, sortDir, sortDataType);
    }, [hasFilter, sortBy, hierarchyDataKey, sortDir, idField, parentIdField, items, sortDataType]);
    return [sortedItems, { sortBy, sortDir }, setSortInfo];
};
export function flatToHierarchyDep(items, id, parentId, isSorted) {
    if (!isSorted)
        throw new Error("Not implemented yet");
    if (!items || items.length === 0) {
        return [];
    }
    const firstTreeItem = new TreeItem(items[0]);
    firstTreeItem.subLevel = 0;
    const stack = [firstTreeItem];
    const _items = [firstTreeItem];
    for (let i = 1; i < items.length; i++) {
        const item = items[i];
        const ti = new TreeItem(item);
        for (let s = stack.length - 1; s >= 0; s--) {
            const lastNode = stack[s];
            if (item[parentId] === lastNode.item[id]) {
                if (!lastNode.children)
                    lastNode.children = [];
                lastNode.children.push(ti);
                stack.push(ti);
                ti.subLevel = stack.length - 1;
                break;
            }
            else {
                ti.subLevel = stack.length - 1;
                stack.pop();
            }
        }
        if (stack.length === 0) {
            _items.push(ti);
            stack.push(ti);
        }
    }
    return _items;
}
const sortHierarchyDep = (data, idField, parentIdField, sortBy) => {
    if (!sortBy)
        return data;
    const sortTree = (data, sortField) => {
        const sortFn = (a, b) => {
            const left = a.item[sortField];
            const right = b.item[sortField];
            return stringSort(left || "", right || "") * -1;
        };
        data.sort(sortFn);
        for (let i = 0; i < data.length; i++) {
            const children = data[i].children;
            if (children && children.length > 0) {
                sortTree(children, sortField);
            }
        }
    };
    const treeItems = flatToHierarchyDep(data, idField, parentIdField, true);
    // const mew = new DataTree<T>(data, idField, parentIdField, true);
    sortTree(treeItems, sortBy);
    return buildFlat(treeItems).map(v => v.item);
};
